import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import type { ReverbConfig } from '../config';
import { createChannelAuthorizer } from './authorizer';
import type { EchoLike } from './client';

/**
 * Constructing the real Echo instance (P0-WEB-05).
 *
 * Isolated from `client.ts` so everything with behaviour worth testing —
 * subscription ref-counting, the monotonic store, the reconnect rule — is
 * testable against `EchoLike` without a socket, a fake server, or a timer.
 */

export interface CreateEchoOptions {
  readonly reverb: ReverbConfig;
  /** Platform API origin, for the channel authorisation endpoint. */
  readonly apiUrl: string;
  readonly fetch?: typeof globalThis.fetch;
  /** Defaults to the page's own protocol. */
  readonly secure?: boolean;
}

export function createEcho({
  reverb,
  apiUrl,
  fetch,
  // wss when the page is https, ws when it is not. Deriving it rather than
  // configuring it removes the one misconfiguration that presents as a socket
  // blocked by the browser as mixed content, with the reason only in a console
  // nobody has open.
  secure = globalThis.location.protocol === 'https:',
}: CreateEchoOptions): EchoLike {
  const echo = new Echo({
    broadcaster: 'reverb',
    key: reverb.key,
    wsHost: reverb.host,
    wsPort: reverb.port,
    wssPort: reverb.port,
    forceTLS: secure,
    // Reverb speaks WebSocket. Leaving the HTTP fallbacks enabled means a
    // network that blocks WebSockets degrades to long-polling against an
    // endpoint Reverb does not serve, and the failure reports as a timeout
    // rather than as "this network blocks WebSockets".
    enabledTransports: ['ws', 'wss'],
    // The app's channels forbid client events (`accept_client_events_from` is
    // 'none' in config/reverb.php), so there is no telemetry worth the
    // round-trip.
    enableStats: false,
    channelAuthorization: {
      customHandler: createChannelAuthorizer(
        fetch === undefined ? { baseUrl: apiUrl } : { baseUrl: apiUrl, fetch },
      ),
    },
    // Echo otherwise patches axios, jQuery, Vue and Turbo globals to add an
    // X-Socket-Id header. The portal uses none of them, and a library reaching
    // into globals it does not own is worth declining even when it is a no-op.
    withoutInterceptors: true,
    Pusher,
  });

  // Narrowed to `EchoLike` on the way out, and structurally rather than by
  // assertion: if Echo's surface changes under us, this stops compiling instead
  // of failing at runtime the first time somebody watches a run.
  return echo;
}
